import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import BoardBlockConfigSidebar from "../../../components/sidebar-components/BoardBlockConfigSidebar.jsx";
import { toast } from "../../../components/ui-components/Toast.jsx";
import { primaryButtonCls } from "../../../components/ui-components/buttonStyles.js";
import { getBoardReport, getBoardReportManifest, updateBoardReport } from "../document/boardReportApi.js";

const findBlock = (pages, blockId) => {
  for (const page of pages) {
    const block = (page.blocks || []).find((b) => b.id === blockId);
    if (block) return block;
  }
  return null;
};

/**
 * ContentPanel - the Content tab of the report home: edits a board report's template (block
 * titles/bindings/optional-visibility over the fixed page structure). Formerly the standalone
 * /template route (BoardReportTemplateEditor); recomposed here as a tab panel so all report editing
 * lives under one home. Same persistence contract: the backend PUT is a full-replace, so Save
 * re-fetches the report immediately before PUTing the whole thing back with the updated
 * manifest_override.blocks accumulator.
 */
const ContentPanel = ({ id }) => {
  const [pages, setPages] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    if (!id) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setLoadError(null);
      const [manifest, reportData] = await Promise.all([
        getBoardReportManifest(id),
        getBoardReport(id),
      ]);
      setPages(manifest?.pages || []);
      setOverrides(reportData?.manifest_override?.blocks || {});
    } catch (err) {
      setLoadError(err.message || "Failed to load template");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedBlock = selectedBlockId ? findBlock(pages, selectedBlockId) : null;

  const handleSelectBlock = (blockId) => setSelectedBlockId(blockId);
  const handleCloseSidebar = () => setSelectedBlockId(null);

  const handleBlockUpdate = (partial) => {
    if (!selectedBlockId) return;
    const currentBlock = findBlock(pages, selectedBlockId);
    if (!currentBlock) return;
    const merged = { ...currentBlock, ...partial };

    setPages((prevPages) =>
      prevPages.map((page) => ({
        ...page,
        blocks: (page.blocks || []).map((b) => (b.id === selectedBlockId ? merged : b)),
      }))
    );
    setOverrides((prev) => ({
      ...prev,
      [selectedBlockId]: { ...(prev[selectedBlockId] || {}), ...partial },
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const current = await getBoardReport(id);
      await updateBoardReport(id, {
        name: current.name,
        type: current.type,
        period: current.period,
        scope: current.scope,
        recipients: current.recipients,
        manifest_override: { blocks: overrides },
      });
      toast.success("Template saved");
    } catch (err) {
      toast.error("Failed to save template");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <div className="text-text-3">Loading…</div>;
  if (loadError) return <div className="text-danger">Failed to load template: {loadError}</div>;

  return (
    <div className="relative" style={{ marginRight: selectedBlock ? 364 : 0 }}>
      <div className="flex items-center justify-between gap-4 mb-5">
        <p className="text-sm text-text-2">
          Page structure is fixed. Click a block to edit its title, binding, or optional visibility.
        </p>
        <button type="button" className={primaryButtonCls} onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="space-y-6">
        {pages.map((page) => (
          <div key={page.id} className="bg-card border border-card-bd rounded-card p-5">
            <h3 className="text-sm font-semibold text-text mb-3">{page.title}</h3>
            <ul className="space-y-2">
              {(page.blocks || []).map((block) => (
                <li key={block.id}>
                  <button
                    type="button"
                    onClick={() => handleSelectBlock(block.id)}
                    style={{ width: "100%" }}
                    className={`box-border flex items-center justify-between text-left p-2 rounded-ctrl border border-solid text-sm transition-colors cursor-pointer ${
                      selectedBlockId === block.id
                        ? "border-primary bg-surface-2 text-text"
                        : "border-border bg-card text-text hover:bg-surface-2"
                    }`}
                  >
                    <span>{block.title}</span>
                    <span className="text-xs text-text-3">
                      {block.type}
                      {block.optional ? " · optional" : ""}
                    </span>
                  </button>
                </li>
              ))}
              {(page.blocks || []).length === 0 && (
                <li className="text-sm text-text-3">No blocks on this page.</li>
              )}
            </ul>
          </div>
        ))}
        {pages.length === 0 && <div className="text-sm text-text-3">No pages in this template.</div>}
      </div>

      <BoardBlockConfigSidebar
        selectedBlock={selectedBlock}
        onUpdate={handleBlockUpdate}
        onClose={handleCloseSidebar}
      />
    </div>
  );
};

ContentPanel.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ContentPanel;
